/**
 * The Content Card step of the Production Line journey (content-card).
 *
 * The merchant reviews what Mouja will make and confirms it or asks for a
 * regeneration; paid media remains behind exact-card confirmation. The
 * v1.12 layout labels this as a summary, not an unavailable full timed script.
 */

import { type ReactNode } from 'react';
import {
  type MerchantCreativeRunView,
  type RegenerationReason,
} from '../../merchant/api.js';
import { type WorkspaceCopy } from '../../merchant/copy.js';
import { Button, Card, Eyebrow, Pix } from '../../merchant/ui/primitives.js';
import { useLanguage } from '../../i18n/language.js';
import { contentReviewCopy } from './contentReviewCopy.js';

const REASONS: readonly RegenerationReason[] = [
  'NOT_ON_BRAND',
  'TOO_SALES_HEAVY',
  'WRONG_FOR_MY_AUDIENCE',
];

export function CardStep({
  copy,
  run,
  busy,
  asking,
  onMakeIt,
  onOpenAngles,
  onCancelAngles,
  onAskAngle,
}: {
  readonly copy: WorkspaceCopy;
  readonly run: MerchantCreativeRunView;
  readonly busy: boolean;
  readonly asking: boolean;
  readonly onMakeIt: () => void;
  readonly onOpenAngles: () => void;
  readonly onCancelAngles: () => void;
  readonly onAskAngle: (reason: RegenerationReason | null) => void;
}): ReactNode {
  const { language } = useLanguage();
  const ui = contentReviewCopy[language];
  const card = run.card;
  if (card === null) return null;
  const left = run.regenerationsRemaining ?? 0;
  return (
    <Card
      as="section"
      padding={24}
      aria-labelledby="card-h"
      className="mj-ws-content-review"
    >
      <Eyebrow>
        <Pix status="needs-input" />
        {ui.step}
      </Eyebrow>
      <h2 id="card-h" className="mj-ws-h2 mj-ws-mt-8">
        {copy.line.cardTitle}
      </h2>
      <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4 }}>
        {ui.scope}
      </p>
      <h3 className="mj-ws-h3 mj-ws-mt-20">{ui.prepared}</h3>
      <p dir="auto" className="mj-ws-strong mj-ws-mt-8">
        {card.direction}
      </p>
      <div className="mj-ws-muted mj-ws-xs mj-ws-mt-12">{copy.line.cardOpens}</div>
      <p className="mj-ws-card-hook" dir="auto">
        “{card.hookLine}”
      </p>
      <div className="mj-ws-muted mj-ws-xs mj-ws-mt-12">{copy.line.cardShows}</div>
      <p className="mj-ws-sm" dir="auto">
        {card.whatTheVideoShows}
      </p>
      <h3 className="mj-ws-h3 mj-ws-mt-20">{ui.format}</h3>
      <dl className="mj-ws-card-facts">
        <div>
          <dt>{copy.line.whoAppears}</dt>
          <dd>{copy.line.noOne}</dd>
        </div>
        <div>
          <dt>{copy.line.length}</dt>
          <dd>{copy.common.seconds(card.durationSeconds)}</dd>
        </div>
        <div>
          <dt>{copy.line.platforms}</dt>
          <dd>{card.targetPlatforms.join(' · ')}</dd>
        </div>
        <div>
          <dt>{copy.line.aspect}</dt>
          <dd dir="ltr">{card.aspectRatio}</dd>
        </div>
        <div>
          <dt>{copy.line.language}</dt>
          <dd dir="ltr">{card.language}</dd>
        </div>
      </dl>
      <div className="mj-ws-muted mj-ws-xs mj-ws-mt-12">{copy.line.assets}</div>
      {card.assets.references.length === 0 ? (
        <p className="mj-ws-sm">{copy.line.noAssets}</p>
      ) : (
        <ul className="mj-ws-sm mj-ws-checklist" style={{ margin: 0 }}>
          {card.assets.references.map((reference) => (
            <li key={reference.referenceId}>
              {reference.sourceUrl === null ? (
                (reference.kind ?? reference.referenceId)
              ) : (
                <a href={reference.sourceUrl} target="_blank" rel="noopener noreferrer">
                  {reference.kind ?? reference.referenceId}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
      {card.whatMoujaStillNeeds.length > 0 ? (
        <>
          <div className="mj-ws-muted mj-ws-xs mj-ws-mt-12">{copy.line.stillNeeds}</div>
          <ul className="mj-ws-sm mj-ws-checklist" style={{ margin: 0 }}>
            {card.whatMoujaStillNeeds.map((item) => (
              <li key={item} dir="auto">
                {item}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="mj-ws-content-approval mj-ws-sm mj-ws-mt-20">{ui.approval}</p>
      {asking ? (
        <div className="mj-ws-angles" role="group" aria-label={copy.line.whyWrong}>
          <span className="mj-ws-muted mj-ws-sm" style={{ width: '100%' }}>
            {copy.line.whyWrong}
          </span>
          {REASONS.map((reason) => (
            <Button
              key={reason}
              small
              disabled={busy}
              onClick={() => {
                onAskAngle(reason);
              }}
            >
              {copy.line[reason]}
            </Button>
          ))}
          <Button
            small
            disabled={busy}
            onClick={() => {
              onAskAngle(null);
            }}
          >
            {copy.line.justAnother}
          </Button>
          <Button variant="quiet" small onClick={onCancelAngles}>
            {copy.common.cancel}
          </Button>
        </div>
      ) : (
        <div className="mj-ws-row mj-ws-mt-24">
          <Button
            variant="primary"
            pixelWave
            aria-busy={busy}
            disabled={busy}
            onClick={onMakeIt}
          >
            {busy ? copy.line.making : ui.approve}
          </Button>
          {left > 0 ? (
            <>
              <Button disabled={busy} onClick={onOpenAngles}>
                {copy.line.differentAngle}
              </Button>
              <span className="mj-ws-muted mj-ws-sm">{copy.line.anglesLeft(left)}</span>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}
